import { Table } from 'antd';
import { FC, useEffect, useState } from 'react';
import styles from './index.module.scss';
import { EllipsisSpan } from '@maxtropy/components';
import { DatabaseOutlined } from '@ant-design/icons';
import {
  AllFeedInQuantityItem,
  BaseElectricityBillResponse,
  getElectricityPhotovoltaicAllFeedInQuantityResponse,
  QuantityMeterGroupsItem,
} from '../../../../api/electricity';
import { useParams } from 'react-router-dom';
import { units } from '../utils';
import {
  HasTimeOfUseElectricityPricePropsType,
  HasTimeOfUseElectricityPriceProps,
  ElectricitySaleContractSettlementMeterType,
} from '../../../../api/electricitySettlementRules';

const columns = [
  {
    title: '',
    dataIndex: 'highLowType',
    ellipsis: { showTitle: true },
    render: (v: HasTimeOfUseElectricityPricePropsType) => (
      <EllipsisSpan value={v in HasTimeOfUseElectricityPricePropsType ? HasTimeOfUseElectricityPriceProps[v] : '总'} />
    ),
    width: 70,
    // className: `${styles.firstCell}`,
  },
  {
    title: '上月示数',
    dataIndex: 'lastQuantity',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v?.toFixed(2)} />,
  },
  {
    title: '本月示数',
    dataIndex: 'currentQuantity',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v?.toFixed(2)} />,
  },
  {
    title: '反向电上月示数',
    dataIndex: 'reverseLastQuantity',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v?.toFixed(2)} />,
  },
  {
    title: '反向电本月示数',
    dataIndex: 'reverseCurrentQuantity',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v?.toFixed(2)} />,
  },
  {
    title: '倍率',
    dataIndex: 'multiplyRatio',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v} />,
  },
  {
    title: '单价',
    dataIndex: 'unitPowerRate',
    ellipsis: { showTitle: true },
    render: (v: number) => (
      <EllipsisSpan value={v !== undefined && v !== null ? `${v?.toFixed(8)} ${units['元/千瓦时']}` : v} />
    ),
  },
  {
    title: '收费电量',
    dataIndex: 'totalActivePowerQuantity',
    ellipsis: { showTitle: true },
    render: (v: number) => (
      <EllipsisSpan value={v !== undefined && v !== null ? `${v?.toFixed(2)} ${units.千瓦时}` : v} />
    ),
  },
  {
    title: '总额',
    dataIndex: 'totalElectricFee',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v !== undefined && v !== null ? `${v?.toFixed(2)} ${units.元}` : v} />,
  },
];

const ElectricityBillDistributedNewEnergyActivePower: FC<{ billInfo?: BaseElectricityBillResponse }> = ({
  billInfo,
}) => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<QuantityMeterGroupsItem[]>([]);

  useEffect(() => {
    if (id) {
      getElectricityPhotovoltaicAllFeedInQuantityResponse(id).then(res => {
        res && setData(res.quantityMeterGroups);
      });
    }
  }, [id]);

  const activePowerTables = data.map(i => {
    const meterType = i.meterType;
    const dataSource = i.quantities.map(item => {
      return {
        id: item.id,
        lastQuantity: item.lastQuantity,
        currentQuantity: item.currentQuantity,
        reverseCurrentQuantity: item.reverseCurrentQuantity,
        reverseLastQuantity: item.reverseLastQuantity,
        multiplyRatio: item.multiplyRatio,
        unitPowerRate: item.unitPowerRate,
        totalActivePowerQuantity: item.totalActivePowerQuantity,
        totalElectricFee: item.totalElectricFee,
        highLowType: item.highLowType,
      };
    });

    const tableDataSource = [
      ...dataSource,
      ...[
        dataSource.reduce(
          (a, b) => {
            return {
              ...a,
              lastQuantity: (a.lastQuantity ?? 0) + (b.lastQuantity ?? 0),
              currentQuantity: (a.currentQuantity ?? 0) + (b.currentQuantity ?? 0),
              reverseCurrentQuantity: (a.reverseCurrentQuantity ?? 0) + (b.reverseCurrentQuantity ?? 0),
              reverseLastQuantity: (a.reverseLastQuantity ?? 0) + (b.reverseLastQuantity ?? 0),
              totalActivePowerQuantity: (a.totalActivePowerQuantity ?? 0) + (b.totalActivePowerQuantity ?? 0),
              totalElectricFee: (a.totalElectricFee ?? 0) + (b.totalElectricFee ?? 0),
              multiplyRatio: b.multiplyRatio ?? a.multiplyRatio,
            };
          },
          {
            id: '总',
            highLowType: 5,
            lastQuantity: 0,
            currentQuantity: 0,
            reverseCurrentQuantity: 0,
            reverseLastQuantity: 0,
            totalActivePowerQuantity: 0,
            totalElectricFee: 0,
            multiplyRatio: undefined,
          } as unknown as Partial<AllFeedInQuantityItem>
        ),
      ],
    ];

    const phTableDataSource = [
      ...dataSource,
      ...[
        dataSource.reduce(
          (a, b) => {
            return {
              ...a,
              lastQuantity: (a.lastQuantity ?? 0) + (b.lastQuantity ?? 0),
              currentQuantity: (a.currentQuantity ?? 0) + (b.currentQuantity ?? 0),
              totalActivePowerQuantity: (a.totalActivePowerQuantity ?? 0) + (b.totalActivePowerQuantity ?? 0),
              totalElectricFee: (a.totalElectricFee ?? 0) + (b.totalElectricFee ?? 0),
              multiplyRatio: b.multiplyRatio ?? a.multiplyRatio,
            };
          },
          {
            id: '总',
            highLowType: 5,
            lastQuantity: 0,
            currentQuantity: 0,
            reverseCurrentQuantity: null,
            reverseLastQuantity: null,
            totalActivePowerQuantity: 0,
            totalElectricFee: 0,
            multiplyRatio: undefined,
          } as unknown as Partial<AllFeedInQuantityItem>
        ),
      ],
    ];

    return (
      <div key={i.meterId}>
        <div>
          <DatabaseOutlined style={{ color: 'var(--primary-color)', fontSize: 18 }} />
          设备名称：{i.deviceName}
        </div>
        <div className={styles.activePowerTable}>
          <Table
            rowKey="highLowType"
            pagination={false}
            dataSource={
              meterType === ElectricitySaleContractSettlementMeterType.PHOTOVOLTAIC
                ? phTableDataSource
                : tableDataSource
            }
            columns={columns}
          />
        </div>
      </div>
    );
  });

  return <>{activePowerTables}</>;
};

export default ElectricityBillDistributedNewEnergyActivePower;
